import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HitApi } from '../../../Api/ApiHit';
import { addAppVersion, updateAppVersion, searchAppVersion } from '../../../constant/Constant';
import { ArrowLeft, Save } from 'lucide-react';
import toast from 'react-hot-toast';

const AddVersion = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState({
    version: '',
    buildNumber: '',
    platform: 'both',
    releaseType: 'normal',
    forceUpdate: false,
    refreshClientData: false,
    releaseNotes: '',
    downloadUrl: '',
    releasedBy: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      HitApi({ page: 1, limit: 1000 }, searchAppVersion)
        .then((res) => {
          const record = res?.data?.docs?.find((d) => d._id === id);
          if (record) {
            setForm({
              version: record.version || '',
              buildNumber: record.buildNumber || '',
              platform: record.platform || 'both',
              releaseType: record.releaseType || 'normal',
              forceUpdate: record.forceUpdate || false,
              refreshClientData: record.refreshClientData || false,
              releaseNotes: record.releaseNotes || '',
              downloadUrl: record.downloadUrl || '',
              releasedBy: record.releasedBy || '',
              isActive: record.isActive !== false,
            });
          }
        })
        .catch(() => toast.error('Failed to load version'));
    }
  }, [id, isEdit]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!form.version.trim()) {
      toast.error('Version is required');
      return;
    }
    if (!form.buildNumber) {
      toast.error('Build number is required');
      return;
    }

    setLoading(true);
    const payload = {
      ...form,
      buildNumber: parseInt(form.buildNumber),
    };
    if (isEdit) payload._id = id;

    const api = isEdit ? updateAppVersion : addAppVersion;
    HitApi(payload, api)
      .then((res) => {
        if (res?.success) {
          toast.success(isEdit ? 'Version updated' : 'Version created');
          navigate('/app-control/versions');
        } else {
          toast.error(res?.message || 'Failed to save');
        }
      })
      .catch(() => toast.error('Failed to save'))
      .finally(() => setLoading(false));
  };

  return (
    <div className="p-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/app-control/versions')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Release' : 'New Release'}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Configure app version and update policy</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow border border-gray-100 p-6 space-y-5">
        {/* Version + Build */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Version *</label>
            <input
              type="text"
              placeholder="e.g. 1.2.0"
              value={form.version}
              onChange={(e) => handleChange('version', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Build Number *</label>
            <input
              type="number"
              placeholder="e.g. 12"
              value={form.buildNumber}
              onChange={(e) => handleChange('buildNumber', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">Must be higher than previous builds</p>
          </div>
        </div>

        {/* Platform */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Platform</label>
          <div className="flex gap-3">
            {[
              { value: 'both', label: 'Both' },
              { value: 'android', label: 'Android' },
              { value: 'ios', label: 'iOS' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleChange('platform', opt.value)}
                className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                  form.platform === opt.value
                    ? 'border-black bg-black text-white'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Release Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Release Type</label>
          <div className="flex gap-3">
            <button
              onClick={() => handleChange('releaseType', 'normal')}
              className={`flex-1 p-4 rounded-xl border-2 transition-all text-left ${
                form.releaseType === 'normal'
                  ? 'border-black bg-gray-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className="font-semibold text-sm text-gray-900">Normal Build</p>
              <p className="text-xs text-gray-500 mt-1">Regular update with bug fixes and improvements</p>
            </button>
            <button
              onClick={() => handleChange('releaseType', 'important')}
              className={`flex-1 p-4 rounded-xl border-2 transition-all text-left ${
                form.releaseType === 'important'
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className="font-semibold text-sm text-gray-900">Important Update</p>
              <p className="text-xs text-gray-500 mt-1">Critical update with major features or security fixes</p>
            </button>
          </div>
        </div>

        {/* Force Update */}
        <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100">
          <div>
            <p className="font-semibold text-sm text-gray-900">Force Update</p>
            <p className="text-xs text-gray-500 mt-0.5">Users cannot use the app without updating to this version or newer</p>
          </div>
          <button
            onClick={() => handleChange('forceUpdate', !form.forceUpdate)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.forceUpdate ? 'bg-red-500' : 'bg-gray-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
              form.forceUpdate ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Refresh Client Data */}
        <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
          <div>
            <p className="font-semibold text-sm text-gray-900">Refresh Client Data</p>
            <p className="text-xs text-gray-500 mt-0.5">Re-fetch client details from server when app updates (use when new fields are added to client schema)</p>
          </div>
          <button
            onClick={() => handleChange('refreshClientData', !form.refreshClientData)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.refreshClientData ? 'bg-blue-500' : 'bg-gray-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
              form.refreshClientData ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Release Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Release Notes</label>
          <textarea
            rows={4}
            placeholder="What's new in this version..."
            value={form.releaseNotes}
            onChange={(e) => handleChange('releaseNotes', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-none"
          />
        </div>

        {/* Download URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Download / Store URL</label>
          <input
            type="text"
            placeholder="https://play.google.com/store/apps/..."
            value={form.downloadUrl}
            onChange={(e) => handleChange('downloadUrl', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>

        {/* Released By */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Released By</label>
          <input
            type="text"
            placeholder="Admin name"
            value={form.releasedBy}
            onChange={(e) => handleChange('releasedBy', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>

        {/* Active */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
          />
          <label className="text-sm text-gray-700">Active (visible to version checks)</label>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition-colors disabled:bg-gray-400"
        >
          <Save className="w-4 h-4" />
          {loading ? 'Saving...' : isEdit ? 'Update Release' : 'Create Release'}
        </button>
      </div>
    </div>
  );
};

export default AddVersion;
